
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const START_WAITLIST = 340;
const END_WAITLIST = 347;
const ANIMATION_DURATION = 1200; // ms

const HeroSection: React.FC = () => {
  const [waitlist, setWaitlist] = useState(START_WAITLIST);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let start = START_WAITLIST;
    const increment = (END_WAITLIST - START_WAITLIST) / (ANIMATION_DURATION / 40);
    intervalRef.current = setInterval(() => {
      start += increment;
      if (start >= END_WAITLIST) {
        setWaitlist(END_WAITLIST);
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        setWaitlist(Math.floor(start));
      }
    }, 40);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <section className="relative min-h-[70vh] flex flex-col justify-center items-center bg-gradient-to-br from-sauna-beige via-white to-blue-50 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10 pointer-events-none select-none">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-sauna-orange blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-sauna-blue blur-3xl"></div>
      </div>
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <Logo size="lg" />
        </div>
        {/* Main Headline */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-sauna-navy mb-6 leading-tight">
          Discover Top Saunas & Cold Plunges Near You
        </h1>
        {/* Subheadline */}
        <p className="text-lg md:text-xl lg:text-2xl text-sauna-navy/70 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
          Real-time ratings, authentic setups, and trusted reviews from our wellness community.
        </p>
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
          <Button 
            className="bg-gray-300 text-gray-500 px-8 py-6 text-lg rounded-full w-full sm:w-auto cursor-not-allowed"
            disabled
          >
            Coming Soon
          </Button>
          <Button 
            className="bg-sauna-orange hover:bg-sauna-orange/90 text-white px-8 py-6 text-lg rounded-full w-full sm:w-auto shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 font-semibold"
            onClick={() => document.getElementById('notify-me-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Notify Me When Available
          </Button>
        </div>
        {/* Waitlist Count */}
        <div className="text-sm text-sauna-navy/60 mb-2">
          <span className="font-semibold">{waitlist}</span> wellness fans are already on the waitlist!
        </div>
        {/* Privacy Note */}
        <div className="text-xs text-sauna-navy/40">
          No spam. Only updates about launch.
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-sauna-navy/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-sauna-navy/50 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
